import {
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/CloudDownload";
import { useNavigate } from "react-router-dom";
import DowloadHelper from "@/app/helpers/DownloadHelper";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import { useMutation } from "@tanstack/react-query";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";
import dataService from "@/app/services/dataService";
import { adaptFileResponse } from "@/app/adapter/fileAdapter";
import { ERROR_MESSAGE } from "@/app/constants/constants";

const DOWNLOAD_ERROR = "Failed to download file.";
const LOADING_MESSAGE = "We are preparing your file. Please wait.";

const OrderDetailsHeader = ({
  orderId,
  shouldShowAttachments,
}: {
  orderId: number;
  shouldShowAttachments: boolean;
}) => {
  const { setNotification } = useGlobalNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const downloadClientPDF = useMutation({
    mutationFn: async () => {
      const result = await dataService.getExportOrderDataForPdf(orderId);

      if (result.success) {
        DowloadHelper.downloadOrdersPdf([result.data]);
      } else {
        setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  const printClientPDF = useMutation({
    mutationFn: async () => {
      const result = await dataService.getExportOrderDataForPdf(orderId);
      if (result.success) {
        await DowloadHelper.printOrdersPdf([result.data]);
      } else {
        setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
    },
  });

  const downloadAttachmentsZip = useMutation({
    mutationFn: () => dataService.downloadOrderAttachmentsRaw(orderId),
    onSuccess: (res) => {
      const { blob, filename } = adaptFileResponse(res);
      DowloadHelper.triggerDownload(blob, filename);
    },
    onError: () => {
      setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        gap: 1,
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Button
          onClick={() => navigate("/order/history")}
          startIcon={
            <ArrowBackIosNewIcon fontSize="small" sx={{ color: "#1976d2" }} />
          }
          size={isMobile ? "small" : "medium"}
          className="request-link"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            px: isMobile ? 0.5 : 0,
            minWidth: 0,
            "&:hover": {
              textDecoration: "underline",
              background: "transparent !important",
            },
            color: "#1976d2 !important",
          }}
        >
          Back to All Orders
        </Button>
      </Stack>

      {isMobile ? (
        <Stack direction="row" spacing={0.5} sx={{ ml: "auto" }}>
          {shouldShowAttachments && (
            <IconButton
              size="small"
              onClick={() => downloadAttachmentsZip.mutate()}
              aria-label="Download attachments"
              disabled={downloadAttachmentsZip.isPending}
            >
              <DownloadIcon fontSize="small" sx={{ color: "#1976d2" }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => printClientPDF.mutate()}
            aria-label="Print"
            disabled={printClientPDF.isPending}
          >
            <PrintIcon fontSize="small" sx={{ color: "#1976d2" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => downloadClientPDF.mutate()}
            aria-label="Open PDF"
          >
            <PictureAsPdfIcon sx={{ color: "#1976d2" }} fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={2}>
          {shouldShowAttachments && (
            <Button
              onClick={() => downloadAttachmentsZip.mutate()}
              disabled={downloadAttachmentsZip.isPending}
              className="request-link"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                "&:hover": {
                  textDecoration: "underline",
                  background: "transparent !important",
                },
                color: "#1976d2 !important",
              }}
              endIcon={<DownloadIcon sx={{ color: "#1976d2" }} />}
            >
              Download Attachments
            </Button>
          )}
          <Button
            onClick={() => printClientPDF.mutate()}
            disabled={printClientPDF.isPending}
            className="request-link"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              "&:hover": {
                textDecoration: "underline",
                background: "transparent !important",
              },
              color: "#1976d2 !important",
            }}
            endIcon={<PrintIcon sx={{ color: "#1976d2" }} />}
          >
            Print
          </Button>
          <Button
            onClick={() => downloadClientPDF.mutate()}
            className="request-link"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              "&:hover": {
                textDecoration: "underline",
                background: "transparent !important",
              },
              color: "#1976d2 !important",
            }}
            endIcon={<PictureAsPdfIcon sx={{ color: "#1976d2" }} />}
          >
            PDF
          </Button>
        </Stack>
      )}

      {(downloadClientPDF.isPending ||
        printClientPDF.isPending ||
        downloadAttachmentsZip.isPending) && (
        <PageLoadingOverlay text={LOADING_MESSAGE} />
      )}
    </Box>
  );
};

export default OrderDetailsHeader;
